import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Icon, type IconName } from '../../components/ui/Icon.js';
import { useMemberPreferences, useSetShowFavoritesBar } from './api.js';
import { useQuickAccess } from './QuickAccessProvider.js';
import { itemKey, type FavoriteItem, type RecentItem } from './store.js';

/** Icon shown next to a record row, by entity type. */
const ENTITY_ICON: Record<string, IconName> = {
  customer: 'customers',
  project: 'projects',
  agreement: 'agreements',
  invoice: 'invoices',
  opportunity: 'reports',
  ticket: 'tickets',
  timeEntry: 'time',
  onboarding: 'approvals',
};

function recordIcon(entity: string): IconName {
  return ENTITY_ICON[entity] ?? 'dashboard';
}

function iconFor(item: FavoriteItem): IconName {
  return item.kind === 'menu' ? item.icon : recordIcon(item.entity);
}

/** A single popover button + panel for the top bar. */
function QuickMenu({
  label,
  icon,
  isOpen,
  onToggle,
  children,
}: {
  label: string;
  icon: IconName;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className={`rounded-lg p-2 hover:bg-slate-100 ${
          isOpen ? 'bg-slate-100 text-slate-700' : 'text-slate-500 hover:text-slate-700'
        }`}
        title={label}
        aria-label={label}
        aria-expanded={isOpen}
      >
        <Icon name={icon} className="h-5 w-5" />
      </button>
      {isOpen ? (
        <div className="absolute right-0 z-40 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          {children}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Top-bar "Recent" (last 10 records viewed/edited) and "Favorites" (pinned menus or
 * records) quick-access menus.
 */
export function QuickAccessMenus(): JSX.Element {
  const navigate = useNavigate();
  const {
    recents,
    favorites,
    toggleFavorite,
    isFavorite,
    removeFavorite,
    moveFavorite,
    clearRecents,
  } = useQuickAccess();
  const { data: preferences } = useMemberPreferences();
  const setShowBar = useSetShowFavoritesBar();
  const [open, setOpen] = useState<'recent' | 'favorites' | null>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(null);
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (path: string): void => {
    setOpen(null);
    navigate(path);
  };

  const renderRecent = (item: RecentItem): JSX.Element => {
    const key = itemKey(item);
    const favorited = isFavorite(key);
    return (
      <li key={key} className="group flex items-center gap-1 pr-1 hover:bg-slate-50">
        <button
          type="button"
          onClick={() => go(item.path)}
          className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left"
        >
          <Icon name={recordIcon(item.entity)} className="h-4 w-4 shrink-0 text-slate-400" />
          <span className="truncate text-sm text-slate-700">{item.label}</span>
        </button>
        <button
          type="button"
          onClick={() => toggleFavorite(item)}
          className={`rounded p-1 ${favorited ? 'text-amber-400' : 'text-slate-300 hover:text-amber-400'}`}
          title={favorited ? 'Remove favorite' : 'Add favorite'}
          aria-label={favorited ? 'Remove favorite' : 'Add favorite'}
        >
          <Icon name="star" className="h-4 w-4" fill={favorited ? 'currentColor' : 'none'} />
        </button>
      </li>
    );
  };

  const renderFavorite = (item: FavoriteItem): JSX.Element => {
    const key = itemKey(item);
    return (
      <li
        key={key}
        draggable
        onDragStart={(event) => {
          event.dataTransfer.effectAllowed = 'move';
          setDragKey(key);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (dragKey && dragKey !== key) moveFavorite(dragKey, key);
        }}
        onDragEnd={() => setDragKey(null)}
        className={`group flex cursor-grab items-center gap-1 pr-1 hover:bg-slate-50 ${
          dragKey === key ? 'opacity-50' : ''
        }`}
      >
        <span className="pl-2 text-slate-300" title="Drag to reorder" aria-hidden="true">
          ⋮⋮
        </span>
        <button
          type="button"
          onClick={() => go(item.path)}
          className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left"
        >
          <Icon name={iconFor(item)} className="h-4 w-4 shrink-0 text-slate-400" />
          <span className="truncate text-sm text-slate-700">{item.label}</span>
          {item.kind === 'menu' ? (
            <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
              Menu
            </span>
          ) : null}
        </button>
        <button
          type="button"
          onClick={() => removeFavorite(key)}
          className="rounded p-1 text-slate-300 opacity-0 hover:text-rose-500 group-hover:opacity-100"
          title="Remove favorite"
          aria-label="Remove favorite"
        >
          <Icon name="close" className="h-4 w-4" />
        </button>
      </li>
    );
  };

  return (
    <div ref={containerRef} className="flex items-center gap-1">
      <QuickMenu
        label="Recent"
        icon="clock"
        isOpen={open === 'recent'}
        onToggle={() => setOpen((prev) => (prev === 'recent' ? null : 'recent'))}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recent</p>
          {recents.length > 0 ? (
            <button
              type="button"
              onClick={clearRecents}
              className="text-xs text-slate-400 hover:text-slate-600"
            >
              Clear
            </button>
          ) : null}
        </div>
        {recents.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-slate-400">
            Records you open will appear here.
          </p>
        ) : (
          <ul className="max-h-96 divide-y divide-slate-50 overflow-y-auto">
            {recents.map(renderRecent)}
          </ul>
        )}
      </QuickMenu>

      <QuickMenu
        label="Favorites"
        icon="star"
        isOpen={open === 'favorites'}
        onToggle={() => setOpen((prev) => (prev === 'favorites' ? null : 'favorites'))}
      >
        <div className="border-b border-slate-100 px-3 py-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Favorites
            </p>
          </div>
          <label className="mt-1.5 flex cursor-pointer items-center gap-2 text-xs text-slate-500">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600"
              checked={preferences?.showFavoritesBar ?? false}
              disabled={setShowBar.isPending}
              onChange={(event) => setShowBar.mutate(event.target.checked)}
            />
            Show favorites across the top of the screen
          </label>
        </div>
        {favorites.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-slate-400">
            Star a menu item or a record to pin it here.
          </p>
        ) : (
          <ul className="max-h-96 divide-y divide-slate-50 overflow-y-auto">
            {favorites.map(renderFavorite)}
          </ul>
        )}
        {favorites.length > 1 ? (
          <p className="border-t border-slate-100 px-3 py-1.5 text-[11px] text-slate-400">
            Drag items to reorder.
          </p>
        ) : null}
      </QuickMenu>
    </div>
  );
}
